import { Injectable } from '@angular/core';
import { EMPTY, Observable, Subject, TimeoutError, interval, throwError } from 'rxjs';
import { take, takeUntil, finalize, timeout, catchError } from 'rxjs/operators';
import { SignalRConnectionService } from './signalr-connection.service';
import { SessionStorageService } from './session-storage.service';
import { GlobalDataService } from './global-data.service';
import { GLOBAL_DATA_KEY } from '@core/configs';
import {
    AvatarProcessingProgress,
    AvatarBatchResult,
    AvatarOptimizationEvent,
    ProcessingStatus
} from '@core/models/avatar-process.model';

@Injectable({ providedIn: 'root' })
export class AvatarOptimizationService {
    private processingStatus = new Map<string, ProcessingStatus>();

    constructor(
        private signalRService: SignalRConnectionService,
        private sessionStorageService: SessionStorageService,
        private globalDataService: GlobalDataService
    ) {
        this.initializeSignalRListeners();
    }

    /**
     * Initialize SignalR listeners to relay events
     */
    private initializeSignalRListeners(): void {
        // These will be automatically relayed to subscribers
        // No need to manually handle here - done in processingAvatars()
    }

    /**
     * Optimize batch of avatar images with real-time progress
     *
     * @param daiCds List of DAI codes to process
     * @param channelId Channel ID for SignalR channel
     * @returns Observable that emits progress/complete/error events
     *
     * Features:
     * - Automatic connection management: connect -> process -> disconnect
     * - Non-blocking: API call returns immediately (202 Accepted)
     * - Real-time updates via SignalR
     * - Auto-cleanup on complete/error
     * - Multiple subscribers supported
     * - Can unsubscribe anytime without affecting processing
     */
    processingAvatars(daiCds: string[], channelId: string): Observable<AvatarOptimizationEvent> {
        if (!daiCds || daiCds.length === 0) {
            return throwError(() => new Error('daiCds cannot be empty'));
        }

        if (!channelId) {
            return throwError(() => new Error('channelId is required'));
        }

        // Create event stream
        const eventStream = new Subject<AvatarOptimizationEvent>();
        const cleanup$ = new Subject<void>();

        // Set status
        this.processingStatus.set(channelId, ProcessingStatus.PROCESSING);

        // Start the processing flow
        this.startProcessingFlow(daiCds, channelId, eventStream, cleanup$);

        // Return observable that auto-cleans up
        return eventStream.asObservable().pipe(
            finalize(() => {
                cleanup$.next();
                cleanup$.complete();
                this.processingStatus.delete(channelId);
            })
        );
    }

    /**
     * Start the processing flow over the dealer-owned persistent socket:
     * 1. Ensure the shared socket is started (reused across batches)
     * 2. Join this batch's group (random channelId) and set up channel-filtered listeners
     * 3. Invoke the batch API
     * 4. On completion/error, leave the group — do NOT tear down the socket
     *    (the dealer closes it on idle). Completion is driven by
     *    BatchProcessingComplete/ProcessingError, never by a disconnect.
     */
    private async startProcessingFlow(
        daiCds: string[],
        channelId: string,
        eventStream: Subject<AvatarOptimizationEvent>,
        cleanup$: Subject<void>
    ): Promise<void> {
        // Channel-filtered listeners so this batch only sees its own events,
        // even when the socket is shared with another batch.
        this.listenToImageProcessing(channelId, eventStream, cleanup$);
        this.listenToImageProcessed(channelId, eventStream, cleanup$);
        this.listenToBatchComplete(channelId, eventStream, cleanup$);
        this.listenToError(channelId, eventStream, cleanup$);

        // Keepalive runs while this batch is in flight (batches over ALB idle
        // timeout need it); stop it when the batch cleans up.
        this.startKeepalive(cleanup$);

        // Leave the group when the batch finishes (complete/error/unsubscribe).
        cleanup$.pipe(take(1)).subscribe(() => {
            this.signalRService.leaveGroup(channelId);
        });

        try {
            await this.signalRService.ensureConnected();
            await this.signalRService.joinGroup(channelId);

            const token = this.sessionStorageService.getAccessToken();
            const corpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix) ?? '';
            const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd) ?? '';

            // Invoke over the existing WebSocket — ensures same ECS task. corpPrefix
            // + officeCd are forwarded so background convert logs carry that context.
            await this.signalRService.invoke('StartBatchProcessing', channelId, daiCds, corpPrefix, officeCd, token);
            // Completion/error handled by the listeners above.
        } catch (err: any) {
            if (eventStream.closed) return;
            // Connect/join/invoke failed → transient: surface as an error so the
            // dealer can re-request later (it does NOT blacklist on transient).
            console.log('[AvatarProcess] Failed to start batch for channel:', channelId, err);
            eventStream.error({
                type: 'error',
                channelId,
                data: { message: err?.message || 'Failed to start batch', error: err }
            });
            cleanup$.next();
        }
    }

    /**
     * Close the shared socket. Called by the dealer's idle-close — not per batch.
     */
    async closeConnection(): Promise<void> {
        await this.signalRService.disconnect();
    }

    private startKeepalive(stop$: Subject<void>): void {
        interval(55000).pipe(takeUntil(stop$)).subscribe(async () => {
            try {
                await this.signalRService.invoke('Ping');
            } catch {
                // Ignore — connection drop is detected separately via onclose
            }
        });
    }

    /**
     * Listen to ImageProcessing events (when each image starts processing)
     */
    private listenToImageProcessing(
        channelId: string,
        eventStream: Subject<AvatarOptimizationEvent>,
        cleanup$: Subject<void>
    ): void {
        this.signalRService.on<any>('ImageProcessing', channelId)
            .pipe(
                takeUntil(cleanup$)
            )
            .subscribe({
                next: (data) => { }
            });
    }

    /**
     * Listen to ImageProcessed events (when each image completes)
     * This provides progress updates
     */
    private listenToImageProcessed(
        channelId: string,
        eventStream: Subject<AvatarOptimizationEvent>,
        cleanup$: Subject<void>
    ): void {
        let processedCount = 0;
        let totalCount = 0;

        this.signalRService.on<any>('ImageProcessed', channelId)
            .pipe(
                takeUntil(cleanup$)
            )
            .subscribe({
                next: (data) => {
                    // ImageProcessed event: { DaiCd, Status, S3Key, OriginalSizeKB, WebPSizeKB, SavingsPercent }
                    processedCount++;
                    const source = (data?.arguments || []).length > 0 ? data.arguments[0] : null;
                    // Emit as progress event with image result
                    eventStream.next({
                        type: 'progress',
                        channelId,
                        data: {
                            currentIndex: processedCount,
                            totalImages: totalCount || processedCount, // Will be updated by BatchProcessingComplete
                            percentage: totalCount > 0 ? Math.round((processedCount / totalCount) * 100) : 0,
                            currentDaiCd: source?.daiCd || "",
                            message: source?.status === 'completed' ? 'Processing completed' : data.error || '',
                            imageResult: source?.s3Key ? {
                                daiCd: source?.daiCd || "",
                                cdnUrl: source?.s3Key || "",
                                status: source?.status === 'completed' ? 'success' : 'failed'
                            } : undefined
                        } as AvatarProcessingProgress
                    });
                }
            });
    }

    /**
     * Listen to BatchProcessingComplete event (final results)
     */
    private listenToBatchComplete(
        channelId: string,
        eventStream: Subject<AvatarOptimizationEvent>,
        cleanup$: Subject<void>
    ): void {
        this.signalRService.on<any>('BatchProcessingComplete', channelId)
            .pipe(
                take(1),
                timeout(600000), // 10 phút
                catchError(err => {
                    if (err instanceof TimeoutError) {
                        console.log('[AvatarProcess] Timeout after 10 minutes for channel:', channelId);
                        if (!eventStream.closed) {
                            eventStream.next({ type: 'complete', channelId, data: this.emptyBatchResult(channelId) });
                            eventStream.complete();
                            cleanup$.next(); // leaves the group; socket stays up for the dealer to idle-close
                        }
                    }
                    return EMPTY;
                }),
                takeUntil(cleanup$)
            )
            .subscribe({
                next: (data) => {
                    // BatchProcessingComplete event: { TotalProcessed, CacheHits, NewConversions, Failed, Results }
                    this.processingStatus.set(channelId, ProcessingStatus.COMPLETED);
                    const res = Array.isArray(data.arguments) ? data.arguments[0] : null;
                    // Map backend response to AvatarBatchResult
                    const result: AvatarBatchResult = {
                        channelId: channelId,
                        totalProcessed: res?.totalProcessed ||  0,
                        successCount: (res?.cacheHits || 0) + (res?.newConversions || 0),
                        failedCount: res?.failed || 0,
                        skippedCount: 0,
                        results: (res?.results || []).map((r: any) => ({
                            daiCd: r.daiCd,
                            cdnUrl: r.s3Key || '',
                            status: r.cacheHit ? 'skipped' : 'success'
                        })),
                        totalSavingsKB: 0, // Calculate if needed
                        averageSavingsPercent: 0 // Calculate if needed
                    };

                    // Calculate totals
                    result.totalSavingsKB = result.results.reduce(
                        (sum, r) => sum + (r.originalSizeKB - r.webpSizeKB),
                        0
                    );
                    result.averageSavingsPercent = result.results.length > 0
                        ? result.results.reduce((sum, r) => sum + r.savings, 0) / result.results.length
                        : 0;

                    console.log(`[AvatarProcess] batch complete channel=${channelId} total=${result.totalProcessed} success=${result.successCount} failed=${result.failedCount}`);
                    eventStream.next({
                        type: 'complete',
                        channelId,
                        data: result
                    });
                    eventStream.complete();
                    cleanup$.next(); // leaves the group; dealer owns socket idle-close
                }
            });
    }

    /**
     * Listen to error events (ProcessingError)
     */
    private listenToError(
        channelId: string,
        eventStream: Subject<AvatarOptimizationEvent>,
        cleanup$: Subject<void>
    ): void {
        this.signalRService.on<any>('ProcessingError', channelId)
            .pipe(
                take(1), // Error only fires once
                takeUntil(cleanup$)
            )
            .subscribe({
                next: (data) => {
                    // ProcessingError event: { channelId, error }
                    console.warn(`[AvatarProcess] ProcessingError channel=${channelId}:`, data?.error || data?.Error);
                    this.processingStatus.set(channelId, ProcessingStatus.FAILED);
                    eventStream.error({
                        type: 'error',
                        channelId,
                        data: {
                            message: data.error || data.Error || 'Processing failed',
                            error: data
                        }
                    });
                    cleanup$.next(); // leaves the group; socket stays up for the dealer to idle-close
                }
            });
    }

    /**
     * Get current processing status for a channel
     */
    getStatus(channelId: string): ProcessingStatus {
        return this.processingStatus.get(channelId) || ProcessingStatus.IDLE;
    }

    /**
     * Check if processing is in progress for a channel
     */
    isProcessing(channelId: string): boolean {
        return this.getStatus(channelId) === ProcessingStatus.PROCESSING;
    }

    /**
     * Clear status for a channel (cleanup)
     */
    clearStatus(channelId: string): void {
        this.processingStatus.delete(channelId);
    }

    private emptyBatchResult(channelId: string): AvatarBatchResult {
        return {
            channelId,
            totalProcessed: 0,
            successCount: 0,
            failedCount: 0,
            skippedCount: 0,
            results: [],
            totalSavingsKB: 0,
            averageSavingsPercent: 0
        };
    }
}